import ClientLightbox, {
  CompareSlide,
  CustomSlide,
} from '@/components/lightbox/clientLightbox'
import LightBoxImageRow from '@/components/lightbox/LightboxImageRow'
import SideBySideCompareDialog from '@/components/lightbox/SideBySideCompareDialog'
import { ClientGalleryLoadingState } from '@/components/client/ClientTabLoadingStates'
import { Card } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { useMainScrollableElement } from '@/lib/hooks/useMainScrollableElement'
import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Images } from 'lucide-react'
import { SlideImage } from 'yet-another-react-lightbox'
import { useVirtualizer } from '@tanstack/react-virtual'
import { trpc } from '@/lib/trpc/client'

const formatDateLabel = (dateValue: string) =>
  new Date(dateValue).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/gallery',
)({
  component: Gallery,
  pendingComponent: ClientGalleryLoadingState,
})

function Gallery() {
  const { clientId } = Route.useParams()
  const { data, isLoading } = trpc.clients.listGallery.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )
  const responses = data ?? []

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [slides, setSlides] = useState<CustomSlide[]>([])
  const [comparing, setComparing] = useState(false)
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [selectionMode, setSelectionMode] = useState(false)
  const [sideBySideOpen, setSideBySideOpen] = useState(false)

  const listRef = useRef<HTMLDivElement>(null)
  const scrollElement = useMainScrollableElement()
  const [listOffsetTop, setListOffsetTop] = useState(0)

  useLayoutEffect(() => {
    if (!scrollElement) return

    const updateListOffset = () => {
      if (!listRef.current || !scrollElement) return
      const listRect = listRef.current.getBoundingClientRect()
      const scrollRect = scrollElement.getBoundingClientRect()
      setListOffsetTop(listRect.top - scrollRect.top + scrollElement.scrollTop)
    }

    updateListOffset()

    const resizeObserver = new ResizeObserver(updateListOffset)
    resizeObserver.observe(scrollElement)
    if (listRef.current?.parentElement) {
      resizeObserver.observe(listRef.current.parentElement)
    }

    window.addEventListener('resize', updateListOffset)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateListOffset)
    }
  }, [scrollElement, responses.length])

  const virtualizer = useVirtualizer({
    count: responses.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => 300,
    overscan: 3,
    scrollMargin: listOffsetTop,
  })

  const compareImageSlides = useMemo<SlideImage[]>(() => {
    const uniqueImages = new Map<string, { id: string; url: string }>()
    responses.forEach((response) => {
      response.images.forEach((image) => {
        if (!uniqueImages.has(image.id)) {
          uniqueImages.set(image.id, image)
        }
      })
    })

    return [...uniqueImages.values()].map((image) => ({
      id: image.id,
      src: image.url,
      download: { url: image.url, filename: image.id },
    }))
  }, [responses])

  const imageDateByKey = useMemo<Record<string, string>>(() => {
    const dateMap: Record<string, string> = {}
    responses.forEach((response) => {
      const dateLabel = formatDateLabel(response.response_date)
      response.images.forEach((image) => {
        if (!dateMap[image.id]) {
          dateMap[image.id] = dateLabel
        }
      })
    })
    return dateMap
  }, [responses])

  const selectedCompareSlides = useMemo(
    () =>
      selectedImages
        .map((imageId) =>
          compareImageSlides.find(
            (slide) =>
              typeof slide.download === 'object' &&
              slide.download !== null &&
              slide.download.filename === imageId,
          ),
        )
        .filter((slide): slide is SlideImage => Boolean(slide)),
    [selectedImages, compareImageSlides],
  )

  const openSliderCompare = (leftImage: SlideImage, rightImage: SlideImage) => {
    const compSlide: CompareSlide = {
      type: 'compare',
      before: leftImage,
      after: rightImage,
    }
    setSlides([compSlide])
    setComparing(true)
    setIndex(0)
    setOpen(true)
  }

  const handleImageSelect = (imageId: string) => {
    if (!selectionMode) return
    setSelectedImages((prev) => {
      if (prev.includes(imageId)) {
        return prev.filter((id) => id !== imageId)
      }
      if (prev.length === 2) {
        return [prev[1], imageId]
      }
      return [...prev, imageId]
    })
  }

  useEffect(() => {
    if (!selectionMode || selectedImages.length !== 2) return
    setSideBySideOpen(true)
    setSelectionMode(false)
  }, [selectionMode, selectedImages])

  if (isLoading) {
    return <ClientGalleryLoadingState />
  }

  return (
    <>
      <SideBySideCompareDialog
        open={sideBySideOpen}
        onOpenChange={(open) => {
          setSideBySideOpen(open)
          if (!open) {
            setSelectedImages([])
            setSelectionMode(false)
          }
        }}
        images={compareImageSlides}
        imageDateByKey={imageDateByKey}
        onOpenSliderCompare={openSliderCompare}
        initialLeftKey={
          selectedCompareSlides[0]
            ? (typeof selectedCompareSlides[0].download === 'object' &&
                selectedCompareSlides[0].download !== null &&
                selectedCompareSlides[0].download.filename) ||
              selectedCompareSlides[0].src
            : undefined
        }
        initialRightKey={
          selectedCompareSlides[1]
            ? (typeof selectedCompareSlides[1].download === 'object' &&
                selectedCompareSlides[1].download !== null &&
                selectedCompareSlides[1].download.filename) ||
              selectedCompareSlides[1].src
            : undefined
        }
      />

      <ClientLightbox
        open={open}
        onOpenChange={(open) => {
          setOpen(open)
          if (!open) {
            setSlides([])
            setComparing(false)
            setIndex(0)
          }
        }}
        options={{
          slides: slides,
          index: index,
        }}
        compare={comparing}
      />

      <div className="ph-no-capture w-full md:w-[90%] mx-auto">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-sm opacity-70">
            {selectionMode
              ? selectedImages.length < 2
                ? `Select ${2 - selectedImages.length} image${2 - selectedImages.length === 1 ? '' : 's'} to compare...`
                : 'Opening compare...'
              : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={selectionMode ? 'secondary' : 'outline'}
              size="sm"
              disabled={compareImageSlides.length < 2}
              onClick={() => {
                if (selectionMode) {
                  setSelectionMode(false)
                  setSelectedImages([])
                } else {
                  setSelectionMode(true)
                  setSelectedImages([])
                }
              }}
            >
              <Images className="mr-2 h-4 w-4" />
              {selectionMode ? 'Cancel Compare' : 'Compare'}
            </Button>
          </div>
        </div>
        <Card className="p-4 mb-4">
          {responses.length === 0 && (
            <Empty className="border-0 py-16">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Images className="size-6" />
                </EmptyMedia>
                <EmptyTitle>No gallery images yet</EmptyTitle>
                <EmptyDescription>
                  This client hasn&apos;t uploaded any progress photos.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {responses.length > 0 && (
            <div
              ref={listRef}
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const response = responses[virtualItem.index]
                const imgs = response.images
                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start - virtualizer.options.scrollMargin}px)`,
                    }}
                  >
                    <div className="mb-4">
                      <p className="mb-2 text-sm text-muted-foreground">
                        {formatDateLabel(response.response_date)}
                      </p>
                      <div className="flex">
                        <LightBoxImageRow
                          selectedImages={selectedImages}
                          onSelect={handleImageSelect}
                          inSelectionMode={selectionMode}
                          images={imgs}
                          clientId={clientId}
                          setSlides={(options) => {
                            setSlides(options.slides)
                            setComparing(false)
                            setIndex(options.index ?? 0)
                            setOpen(true)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      </div>
    </>
  )
}
